import { FieldProps } from '@rjsf/utils';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { Alert, Autocomplete } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { TowerModelDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { TextField } from '../../../../ui/components/pure/text-field/text-field.component';
import { HubLayoutTowerRow, LockedKiosk } from './form';

export interface HubLayoutTowersFieldContext {
  towerModels: TowerModelDto[];
  lockedKiosk?: LockedKiosk;
  isAllLocked?: boolean;
  initialTowerKeys?: Set<string>;
  isSetupComplete?: boolean;
  sidesWarning?: string | null;
}

function towerKey(model: string, inventoryID: string): string {
  return `${model}|${inventoryID}`;
}

export function HubLayoutTowersField(props: FieldProps) {
  const ctx = (props.registry.formContext ?? {}) as HubLayoutTowersFieldContext;
  const { towerModels = [], lockedKiosk, isAllLocked, initialTowerKeys, isSetupComplete, sidesWarning } = ctx;

  const towers: HubLayoutTowerRow[] = Array.isArray(props.formData) ? props.formData : [];
  const itemSchema = (props.schema as any)?.items as Record<string, any> | undefined;
  const requiredFields: string[] = itemSchema?.required ?? [];
  const itemErrors = (props.errorSchema as any) ?? {};

  const allModelOptions = towerModels.map((m) => ({ label: m.name, value: m.name }));
  // Once setup is complete, kiosk models are no longer selectable for new/non-kiosk rows
  const nonKioskModelOptions = towerModels
    .filter((m) => m.kioskPosition === undefined)
    .map((m) => ({ label: m.name, value: m.name }));

  const hasFieldError = (index: number, field: string): boolean => {
    return !!itemErrors?.[index]?.[field]?.__errors?.length;
  };

  const updateTowers = (newTowers: HubLayoutTowerRow[]) => {
    props.onChange(newTowers, props.fieldPathId.path);
  };

  const handleAddRow = () => {
    updateTowers([...towers, { model: '', inventoryID: '' }]);
  };

  const handleRemoveRow = (index: number) => {
    updateTowers(towers.filter((_, i) => i !== index));
  };

  const handleUpdateRow = (index: number, field: keyof HubLayoutTowerRow, value: string) => {
    const updated = towers.map((row, i) => {
      if (i !== index) return row;
      const next = { ...row, [field]: value };
      // If the model changes on an existing tower, clear the inventoryID so it can be re-entered
      if (field === 'model' && row.inventoryID && initialTowerKeys?.has(towerKey(row.model, row.inventoryID))) {
        next.inventoryID = '';
      }
      return next;
    });
    updateTowers(updated);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...towers];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    updateTowers(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === towers.length - 1) return;
    const updated = [...towers];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    updateTowers(updated);
  };

  return (
    <Box style={{ marginLeft: '4px', marginRight: '4px' }}>
      {sidesWarning && (
        <Box style={{ marginBottom: '12px' }}>
          <Alert severity="warning">{sidesWarning}</Alert>
        </Box>
      )}
      {towers.map((tower, index) => {
        // Kiosk row: identified by both model AND inventoryID to avoid false matches
        const isKioskRow =
          isAllLocked ||
          !!(lockedKiosk && tower.model === lockedKiosk.model && tower.inventoryID === lockedKiosk.inventoryID);
        // After setup, kiosk models are hidden from non-kiosk rows.
        // The kiosk row itself keeps allModelOptions so its selected value renders on the disabled dropdown.
        const modelOptions = isSetupComplete && !isKioskRow ? nonKioskModelOptions : allModelOptions;
        // InventoryID locked for any existing (server-saved) tower whose composite key hasn't changed
        const isInventoryIDLocked =
          isKioskRow ||
          !!(tower.model && tower.inventoryID && initialTowerKeys?.has(towerKey(tower.model, tower.inventoryID)));
        return (
          <Grid
            container
            spacing={1}
            key={index}
            style={{
              border: '1px solid #e0e0e0',
              borderBottom: index === towers.length - 1 ? '1px solid #e0e0e0' : 'none',
              padding: '8px'
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid style={{ width: '30px' }}>
              <Typography variant="body2" color="textSecondary">
                {index}
              </Typography>
            </Grid>
            {!isAllLocked && (
              <Grid style={{ width: '56px', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <IconButton
                  size="small"
                  onClick={() => handleMoveUp(index)}
                  style={{ visibility: index === 0 ? 'hidden' : 'visible' }}
                >
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleMoveDown(index)}
                  style={{ visibility: index === towers.length - 1 ? 'hidden' : 'visible' }}
                >
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Grid>
            )}
            <Grid size={3}>
              <Autocomplete
                key={tower.model ? `model-${index}-set` : `model-${index}-unset`}
                options={modelOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                onChange={(_, selected) => handleUpdateRow(index, 'model', selected?.value ?? '')}
                value={modelOptions.find((o) => o.value === tower.model) ?? null}
                disabled={isKioskRow}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required={requiredFields.includes('model')}
                    error={hasFieldError(index, 'model')}
                    helperText={hasFieldError(index, 'model') ? 'This field is required' : ' '}
                    textFieldVariant="floating"
                    placeholder="Model"
                    style={{ width: '100%' }}
                  />
                )}
                style={{ width: '100%' }}
              />
            </Grid>
            <Grid size="grow">
              <TextField
                fullWidth
                required={requiredFields.includes('inventoryID')}
                error={hasFieldError(index, 'inventoryID')}
                helperText={hasFieldError(index, 'inventoryID') ? 'This field is required' : ' '}
                textFieldVariant="floating"
                placeholder="Inventory ID"
                value={tower.inventoryID}
                onChange={(e) => handleUpdateRow(index, 'inventoryID', e.target.value)}
                disabled={isInventoryIDLocked}
                style={{ width: '80%' }}
              />
            </Grid>
            <Grid style={{ width: '40px', display: 'flex', alignItems: 'center' }}>
              <IconButton size="small" onClick={() => handleRemoveRow(index)} disabled={isKioskRow}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
      {!isAllLocked && (
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleAddRow}
          style={{ marginTop: '24px', marginLeft: '-4px' }}
        >
          + Add another
        </Button>
      )}
    </Box>
  );
}
